import React, { useState, useEffect } from "react";
import { ArrowRight, Loader2, User, Building2 } from "lucide-react";
import { ERROR } from "@/styles/tokens";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import { useAuthStore } from "@/stores/auth/auth-store";
import { toast } from "sonner";
import * as errorUtils from '@/utils/errors';

interface WorkflowTransferDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflowId: string;
  workflowName: string;
  currentOwnerType: "User" | "Organization";
  currentOwnerId: string;
  currentOwnerName?: string;
  workflowEngineUrl: string;
  onTransferComplete?: () => void;
}

/**
 * Workflow Transfer Dialog
 *
 * Allows users to transfer workflow ownership:
 * - User -> Organization (user must be org member)
 * - Organization -> User (user must be org admin, transfers to self)
 * - Organization -> Organization (user must be admin of source, member of target)
 *
 * Transfers are immediate (no approval required).
 */
export const WorkflowTransferDialog: React.FC<WorkflowTransferDialogProps> = ({
  open,
  onOpenChange,
  workflowId,
  workflowName,
  currentOwnerType,
  currentOwnerId,
  currentOwnerName,
  workflowEngineUrl,
  onTransferComplete,
}) => {
  const organizations = useOrganizationStore((state) => state.organizations);
  const user = useAuthStore((state) => state.user);
  const accessToken = useAuthStore((state) => state.tokens?.accessToken ?? null);

  const [selectedTarget, setSelectedTarget] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFetchingOrgs, setIsFetchingOrgs] = useState(false);

  // Fetch organizations when dialog opens
  useEffect(() => {
    if (open) {
      setIsFetchingOrgs(true);
      const { fetchOrganizations } = useOrganizationStore.getState();

      fetchOrganizations()
        .then(() => {
          setIsFetchingOrgs(false);
        })
        .catch((error) => {
          console.error("Failed to fetch organizations:", error);
          toast.error("Failed to load organizations");
          setIsFetchingOrgs(false);
        });
    }
  }, [open]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedTarget("");
    }
  }, [open]);

  // Build transfer options
  const getTransferOptions = () => {
    const options: { value: string; label: string; type: "User" | "Organization"; icon: React.ReactNode }[] = [];

    // If currently owned by org, add "Personal" option to transfer to self
    if (currentOwnerType === "Organization" && user?.id) {
      options.push({
        value: `user:${user.id}`,
        label: "Personal (My Workflows)",
        type: "User",
        icon: <User className="w-4 h-4" />,
      });
    }

    // Add organizations (excluding current owner if it's an org)
    organizations.forEach((org) => {
      if (currentOwnerType === "Organization" && org._id === currentOwnerId) {
        return; // Skip current owner
      }
      options.push({
        value: `org:${org._id}`,
        label: org.name,
        type: "Organization",
        icon: <Building2 className="w-4 h-4" />,
      });
    });

    return options;
  };

  const transferOptions = getTransferOptions();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedTarget) {
      toast.error("Please select a transfer destination");
      return;
    }

    setIsSubmitting(true);

    try {
      const [targetType, targetId] = selectedTarget.split(":");
      const targetOwnerType = targetType === "user" ? "User" : "Organization";

      const headers: Record<string, string> = {
        "Accept": "application/json",
        "Content-Type": "application/json",
      };

      if (accessToken) {
        headers["Authorization"] = `Bearer ${accessToken}`;
      }

      // Remove trailing slash from URL to avoid double slashes
      const baseUrl = workflowEngineUrl.replace(/\/+$/, '');
      const response = await fetch(`${baseUrl}/api/workflows/${workflowId}/transfer`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          targetOwnerType,
          targetOwnerId: targetId,
        }),
      });

      const payload = await response.json().catch(() => ({ success: false }));

      if (!response.ok || !payload.success) {
        const message = payload.message || payload.error || `Request failed with status ${response.status}`;
        throw new Error(message);
      }

      const targetName = transferOptions.find(opt => opt.value === selectedTarget)?.label || "new owner";
      toast.success(`Workflow "${workflowName}" transferred to ${targetName}`);

      onOpenChange(false);
      onTransferComplete?.();
    } catch (error: unknown) {
      console.error("Failed to transfer workflow:", error);
      toast.error(errorUtils.getErrorMessageOrFallback(error, "Failed to transfer workflow"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedOption = transferOptions.find((opt) => opt.value === selectedTarget);
  const isFormValid = !!selectedTarget;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Transfer Workflow</DialogTitle>
          <DialogDescription>
            Transfer "{workflowName}" to another owner. This action is immediate.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Transfer visualization */}
          <div
            className="flex items-center justify-center gap-3 py-4 px-2 bg-muted/50 rounded-lg"
            role="status"
            aria-label={`Transfer from ${currentOwnerName || currentOwnerType} to ${selectedOption?.label || 'destination'}`}
          >
            <div className="flex items-center gap-2 text-sm">
              {currentOwnerType === "User" ? (
                <User className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
              ) : (
                <Building2 className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
              )}
              <span className="font-medium truncate max-w-[100px]">
                {currentOwnerName || (currentOwnerType === "User" ? "Personal" : "Organization")}
              </span>
            </div>
            <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" aria-hidden="true" />
            <div className="flex items-center gap-2 text-sm">
              {selectedOption ? (
                <>
                  {selectedOption.icon}
                  <span className="font-medium truncate max-w-[100px]">{selectedOption.label}</span>
                </>
              ) : (
                <span className="text-muted-foreground">Select destination</span>
              )}
            </div>
          </div>

          {/* Destination selector */}
          <div className="space-y-2">
            <Label htmlFor="destination">Transfer to</Label>
            <Select
              value={selectedTarget}
              onValueChange={setSelectedTarget}
              disabled={isFetchingOrgs}
            >
              <SelectTrigger id="destination">
                <SelectValue placeholder={isFetchingOrgs ? "Loading..." : "Select destination"} />
              </SelectTrigger>
              <SelectContent>
                {transferOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      {option.icon}
                      <span>{option.label}</span>
                    </div>
                  </SelectItem>
                ))}
                {transferOptions.length === 0 && !isFetchingOrgs && (
                  <SelectItem value="none" disabled>
                    No available destinations
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {/* Info about execution history */}
          <p className="text-sm text-muted-foreground">
            All execution history will be transferred with the workflow.
          </p>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!isFormValid || isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  Transferring...
                </>
              ) : (
                "Transfer"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
